// src/pages/home/index.jsx

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

import useToken from '../hooks/useToken';
import Skeleton from './skeleton/Skeleton';
import { API_ENDPOINTS } from '../utils/config';

/**
 * TagCloud Component - Displays a list of tags with dynamic font size
 * fetched from a secure API endpoint.
 */
const TagCloud = () => {
    const { token } = useToken();

    const [tags, setTags] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const fetchTags = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);

        try {
            const { data, status } = await axios.get(API_ENDPOINTS.TAG_CLOUD_ENDPOINT, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (status === 200 && Array.isArray(data.items)) {
                setTags(data.items);
            } else {
                setTags([]);
                console.warn('Unexpected response format:', data);
            }
        } catch (error) {
            console.error('Error fetching tags:', error);
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const renderTagList = () => {
        if (isLoading) return <Skeleton type="tagcloud" />;

        if (hasError) {
            return (
                <p className="text-danger">
                    Failed to load tag cloud. Please try again later.
                </p>
            );
        }

        if (!tags.length) {
            return (
                <p className="text-muted">
                    No tags available at the moment.
                </p>
            );
        }

        return (
            <ul className="d-flex align-items-start flex-wrap p-0 m-0 tagcloud-wrapper">
                {tags.map((tag) => (
                    <li key={tag.slug} className="d-flex align-items-start">
                        <Link
                            to={`/tag/${tag.slug}`}
                            className="text-muted text-decoration-none"
                            style={{ fontSize: `${tag.font_size}px` }}
                        >
                            <span>{tag.name}</span>
                        </Link>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div className="common-wrapper mb-4 position-relative">
            <h3 className="mb-4 fw-500 text-uppercase text-4">Tag Cloud</h3>
            {renderTagList()}
        </div>
    );
};

export default TagCloud;
